import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usabilityAdminService } from "../../services";
import HeaderTailwind from "../generic/HeaderTailwind";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis
} from "recharts";

// SUS-Fragen Labels
const SUS_QUESTIONS = [
  "Ich denke, dass ich dieses System gerne regelmäßig nutzen würde.",
  "Ich finde das System unnötig komplex.",
  "Ich finde das System einfach zu bedienen.",
  "Ich denke, dass ich technische Unterstützung benötigen würde, um dieses System zu nutzen.",
  "Ich finde, dass die verschiedenen Funktionen in diesem System gut integriert sind.",
  "Ich finde, dass es zu viele Inkonsistenzen in diesem System gibt.",
  "Ich würde mir vorstellen, dass die meisten Menschen dieses System sehr schnell lernen würden.",
  "Ich finde das System sehr umständlich zu bedienen.",
  "Ich fühle mich sehr sicher bei der Nutzung des Systems.",
  "Ich müsste eine Menge lernen, bevor ich mit diesem System arbeiten könnte."
];

// Rollen-Labels
const ROLE_LABELS = {
  admin: "Administrator",
  scorekeeper: "Scorekeeper",
  caller: "Caller",
  superadmin: "Superadmin",
  user: "User (Zuschauer)"
};

// Boxplot-Komponente
const BoxplotChart = ({ data }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const chartHeight = 350;
  const chartWidth = Math.max(500, data.length * 120);
  const margin = { top: 20, right: 30, bottom: 60, left: 60 };
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const yMin = 0;
  const yMax = 100;
  const yRange = yMax - yMin;
  const boxWidth = 60;
  const spacing = plotWidth / (data.length + 1);

  const scaleY = (value) => {
    return margin.top + plotHeight - ((value - yMin) / yRange) * plotHeight;
  };

  const scaleX = (index) => {
    return margin.left + spacing * (index + 1);
  };

  return (
    <div className="w-full overflow-x-auto relative">
      <svg width={chartWidth} height={chartHeight} className="w-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {/* Y-Achse */}
        <line
          x1={margin.left}
          y1={margin.top}
          x2={margin.left}
          y2={margin.top + plotHeight}
          stroke="#64748b"
          strokeWidth={2}
        />
        {/* Y-Achsen-Labels */}
        {[0, 20, 40, 60, 80, 100].map((value) => (
          <g key={value}>
            <line
              x1={margin.left - 5}
              y1={scaleY(value)}
              x2={margin.left}
              y2={scaleY(value)}
              stroke="#64748b"
              strokeWidth={1}
            />
            <text
              x={margin.left - 10}
              y={scaleY(value) + 4}
              textAnchor="end"
              fill="#64748b"
              fontSize="12"
            >
              {value}
            </text>
          </g>
        ))}
        {/* Y-Achsen-Titel */}
        <text
          x={margin.left - 30}
          y={margin.top + plotHeight / 2}
          textAnchor="middle"
          fill="#64748b"
          fontSize="14"
          fontWeight="500"
          transform={`rotate(-90, ${margin.left - 30}, ${margin.top + plotHeight / 2})`}
        >
          SUS-Score
        </text>

        {/* X-Achse */}
        <line
          x1={margin.left}
          y1={margin.top + plotHeight}
          x2={margin.left + plotWidth}
          y2={margin.top + plotHeight}
          stroke="#64748b"
          strokeWidth={2}
        />

        {/* Boxplots */}
        {data.map((item, index) => {
          const x = scaleX(index);
          const yMinPos = scaleY(item.min);
          const yQ1Pos = scaleY(item.q1);
          const yMedianPos = scaleY(item.median);
          const yQ3Pos = scaleY(item.q3);
          const yMaxPos = scaleY(item.max);
          const yMeanPos = scaleY(item.mean);
          const boxHeight = yQ1Pos - yQ3Pos;

          return (
            <g key={item.roleName}>
              {/* Whisker: Min zu Q1 */}
              <line
                x1={x}
                y1={yMinPos}
                x2={x}
                y2={yQ1Pos}
                stroke="#94a3b8"
                strokeWidth={2}
              />
              {/* Whisker: Q3 zu Max */}
              <line
                x1={x}
                y1={yQ3Pos}
                x2={x}
                y2={yMaxPos}
                stroke="#94a3b8"
                strokeWidth={2}
              />
              {/* Whisker-Enden (horizontal) */}
              <line
                x1={x - 10}
                y1={yMinPos}
                x2={x + 10}
                y2={yMinPos}
                stroke="#94a3b8"
                strokeWidth={2}
              />
              <line
                x1={x - 10}
                y1={yMaxPos}
                x2={x + 10}
                y2={yMaxPos}
                stroke="#94a3b8"
                strokeWidth={2}
              />
              {/* Box: Q1 bis Q3 */}
              <rect
                x={x - boxWidth / 2}
                y={yQ3Pos}
                width={boxWidth}
                height={boxHeight}
                fill="#cbd5e1"
                stroke="#64748b"
                strokeWidth={1}
              />
              {/* Median-Linie */}
              <line
                x1={x - boxWidth / 2}
                y1={yMedianPos}
                x2={x + boxWidth / 2}
                y2={yMedianPos}
                stroke="#3b82f6"
                strokeWidth={2}
              />
              {/* Durchschnitt (Punkt) */}
              <circle
                cx={x}
                cy={yMeanPos}
                r={4}
                fill="#ef4444"
                stroke="#fff"
                strokeWidth={1}
              />
              {/* X-Achsen-Label */}
              <text
                x={x}
                y={margin.top + plotHeight + 20}
                textAnchor="middle"
                fill="#64748b"
                fontSize="12"
                fontWeight="500"
              >
                {ROLE_LABELS[item.roleName] || item.roleName}
              </text>
              {/* Tooltip-Trigger */}
              <rect
                x={x - boxWidth / 2 - 5}
                y={margin.top}
                width={boxWidth + 10}
                height={plotHeight}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            </g>
          );
        })}
      </svg>

      {/* Tooltips */}
      {data.map((item, index) => {
        if (hoveredIndex !== index) return null;
        const x = scaleX(index);
        return (
          <div
            key={`tooltip-${index}`}
            className="absolute bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 text-sm z-10 pointer-events-none"
            style={{
              left: `${x}px`,
              top: '20px',
              transform: 'translateX(-50%)',
              minWidth: '200px'
            }}
          >
            <div className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {ROLE_LABELS[item.roleName] || item.roleName}
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between gap-4">
                <span className="text-slate-600 dark:text-slate-400">Minimum:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.min}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600 dark:text-slate-400">Q1 (25%):</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.q1}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600 dark:text-slate-400">Median:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">{item.median}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600 dark:text-slate-400">Q3 (75%):</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.q3}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600 dark:text-slate-400">Maximum:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.max}</span>
              </div>
              <div className="flex justify-between gap-4 border-t border-slate-200 dark:border-slate-700 pt-1 mt-1">
                <span className="text-slate-600 dark:text-slate-400">Durchschnitt:</span>
                <span className="font-medium text-red-600 dark:text-red-400">{item.mean.toFixed(1)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600 dark:text-slate-400">Anzahl:</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.count}</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Legende */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-300 dark:bg-slate-600 border border-slate-400 dark:border-slate-500"></div>
          <span className="text-slate-600 dark:text-slate-400">Box (Q1-Q3)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 bg-blue-500"></div>
          <span className="text-slate-600 dark:text-slate-400">Median</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded-full"></div>
          <span className="text-slate-600 dark:text-slate-400">Durchschnitt</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 bg-slate-400"></div>
          <span className="text-slate-600 dark:text-slate-400">Whisker (Min-Max)</span>
        </div>
      </div>
    </div>
  );
};

function UsabilityStatisticsTailwind() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState(['admin', 'scorekeeper', 'caller', 'superadmin', 'user']); // Alle Rollen standardmäßig ausgewählt

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await usabilityAdminService.getStatistics();
      if (response.success) {
        setStatistics(response.data);
      } else {
        setError(response.error || "Fehler beim Laden der Statistiken");
      }
    } catch (err) {
      setError(err.message || "Fehler beim Laden der Statistiken");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600 dark:text-slate-400">Lade Statistiken...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <HeaderTailwind />
        <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-900 dark:text-slate-100 font-medium mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="inline-flex items-center px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-lg hover:bg-primary-dark dark:hover:bg-primary transition-colors"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <HeaderTailwind />
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.usability.statistics.title", "Usability-Test Statistiken")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.usability.statistics.subtitle", "Detaillierte Auswertungen und Diagramme zu den Usability-Tests.")}
                </p>
              </div>
            </div>
          </div>

          {/* Diagramme */}
          {statistics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Boxplot: SUS-Score pro Rolle */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                    <i className="fas fa-chart-box text-slate-500 dark:text-slate-400"></i>
                    SUS-SCORE NACH ROLLE
                  </h3>
                </div>
                <div className="p-6">
                  {statistics.susScoresByRole && statistics.susScoresByRole.length > 0 ? (
                    <div className="w-full">
                      <BoxplotChart data={statistics.susScoresByRole} />
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                      <i className="fas fa-chart-bar text-4xl mb-4"></i>
                      <p>Keine Daten verfügbar</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Balkendiagramm: Durchschnittswerte der SUS-Fragen */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                    <i className="fas fa-chart-bar text-slate-500 dark:text-slate-400"></i>
                    DURCHSCHNITTSWERTE DER SUS-FRAGEN
                  </h3>
                </div>
                <div className="p-6">
                  {statistics.questionAverages && statistics.questionAverages.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart 
                        data={statistics.questionAverages.map((q, idx) => ({
                          ...q,
                          questionLabel: `Frage ${q.questionIndex}`
                        }))}
                        margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="questionLabel" 
                          angle={-45}
                          textAnchor="end"
                          height={100}
                          stroke="#64748b"
                          tick={{ fill: '#64748b', fontSize: 12 }}
                        />
                        <YAxis 
                          label={{ value: 'Durchschnittswert', angle: -90, position: 'insideLeft' }}
                          domain={[0, 5]}
                          stroke="#64748b"
                          tick={{ fill: '#64748b' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`${value.toFixed(2)}`, 'Durchschnitt']}
                          labelFormatter={(label, payload) => {
                            if (payload && payload[0]) {
                              const questionIndex = payload[0].payload.questionIndex;
                              return `Frage ${questionIndex}: ${SUS_QUESTIONS[questionIndex - 1]}`;
                            }
                            return label;
                          }}
                        />
                        <Bar dataKey="averageScore" fill="#3b82f6" name="Durchschnittswert">
                          {statistics.questionAverages.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.averageScore >= 3 ? "#3b82f6" : "#ef4444"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                      <i className="fas fa-chart-bar text-4xl mb-4"></i>
                      <p>Keine Daten verfügbar</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Task-Statistiken pro Rolle */}
          {statistics && statistics.taskStatisticsByRole && Object.keys(statistics.taskStatisticsByRole).length > 0 && (
            <div className="space-y-6 mt-6">
              {Object.entries(statistics.taskStatisticsByRole).map(([roleName, tasks]) => (
                <div key={roleName} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Durchschnittliche Zeit pro Aufgabe */}
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                        <i className="fas fa-clock text-slate-500 dark:text-slate-400"></i>
                        DURCHSCHNITTLICHE ZEIT PRO AUFGABE - {ROLE_LABELS[roleName]?.toUpperCase() || roleName.toUpperCase()}
                      </h3>
                    </div>
                    <div className="p-6">
                      {tasks && tasks.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart 
                            data={tasks}
                            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="taskTitle" 
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              stroke="#64748b"
                              tick={{ fill: '#64748b', fontSize: 11 }}
                            />
                            <YAxis 
                              label={{ value: 'Zeit (s)', angle: -90, position: 'insideLeft' }}
                              stroke="#64748b"
                              tick={{ fill: '#64748b' }}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px'
                              }}
                              formatter={(value) => [`${value} s`, 'Durchschnittliche Zeit']}
                              labelFormatter={(label) => `Aufgabe: ${label}`}
                            />
                            <Bar dataKey="avgDurationSeconds" fill="#3b82f6" name="Durchschnittliche Zeit (s)">
                              {tasks.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill="#3b82f6" />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                          <i className="fas fa-chart-bar text-4xl mb-4"></i>
                          <p>Keine Daten verfügbar</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Erfolgsrate pro Aufgabe */}
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                        <i className="fas fa-check-circle text-slate-500 dark:text-slate-400"></i>
                        ERFOLGSRATE PRO AUFGABE - {ROLE_LABELS[roleName]?.toUpperCase() || roleName.toUpperCase()}
                      </h3>
                    </div>
                    <div className="p-6">
                      {tasks && tasks.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart 
                            data={tasks}
                            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="taskTitle" 
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              stroke="#64748b"
                              tick={{ fill: '#64748b', fontSize: 11 }}
                            />
                            <YAxis 
                              label={{ value: 'Erfolgsrate (%)', angle: -90, position: 'insideLeft' }}
                              domain={[0, 100]}
                              stroke="#64748b"
                              tick={{ fill: '#64748b' }}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#fff', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px'
                              }}
                              formatter={(value) => [`${value}%`, 'Erfolgsrate']}
                              labelFormatter={(label) => `Aufgabe: ${label}`}
                            />
                            <Bar dataKey="successRate" fill="#10b981" name="Erfolgsrate (%)">
                              {tasks.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={entry.successRate >= 70 ? "#10b981" : entry.successRate >= 50 ? "#f59e0b" : "#ef4444"} 
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                          <i className="fas fa-chart-bar text-4xl mb-4"></i>
                          <p>Keine Daten verfügbar</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Scatterplot: Gesamtzeit vs. SUS-Score */}
          {statistics && statistics.scatterData && statistics.scatterData.length > 0 && (
            <div className="mt-6">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-t-xl">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-2">
                      <i className="fas fa-chart-scatter text-slate-500 dark:text-slate-400"></i>
                      GESAMTZEIT VS. SUS-SCORE
                    </h3>
                    {/* Rollen-Filter */}
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                        Rollen:
                      </span>
                      {Object.entries(ROLE_LABELS).map(([roleName, roleLabel]) => {
                        const hasData = statistics.scatterData.some(d => d.roleName === roleName);
                        if (!hasData) return null;
                        
                        const isSelected = selectedRoles.includes(roleName);
                        const colors = {
                          admin: '#2563eb',        // Kräftiges Blau
                          scorekeeper: '#059669',  // Kräftiges Grün
                          caller: '#dc2626',       // Kräftiges Rot
                          superadmin: '#7c3aed',   // Kräftiges Lila
                          user: '#f59e0b'          // Orange/Gold
                        };
                        const roleColor = colors[roleName] || '#64748b';
                        
                        return (
                          <label
                            key={roleName}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedRoles([...selectedRoles, roleName]);
                                  } else {
                                    setSelectedRoles(selectedRoles.filter(r => r !== roleName));
                                  }
                                }}
                                className="sr-only"
                              />
                              <div 
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                                  isSelected
                                    ? 'border-current'
                                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                                } cursor-pointer`}
                                style={isSelected ? { 
                                  backgroundColor: roleColor, 
                                  borderColor: roleColor 
                                } : {}}
                              >
                                {isSelected && (
                                  <i className="fas fa-check text-white text-[10px]"></i>
                                )}
                              </div>
                            </div>
                            <span 
                              className="text-xs font-medium"
                              style={{ color: isSelected ? roleColor : '#94a3b8' }}
                            >
                              {roleLabel}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <ResponsiveContainer width="100%" height={400}>
                    <ScatterChart
                      margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        type="number"
                        dataKey="totalDurationSeconds"
                        name="Gesamtzeit"
                        label={{ value: 'Gesamtzeit (s)', position: 'insideBottom', offset: -10 }}
                        stroke="#64748b"
                        tick={{ fill: '#64748b' }}
                      />
                      <YAxis 
                        type="number"
                        dataKey="susScore"
                        name="SUS-Score"
                        label={{ value: 'SUS-Score', angle: -90, position: 'insideLeft' }}
                        domain={[0, 100]}
                        stroke="#64748b"
                        tick={{ fill: '#64748b' }}
                      />
                      <Tooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name, props) => {
                          if (name === 'susScore') return [`${value}/100`, 'SUS-Score'];
                          if (name === 'totalDurationSeconds') return [`${value} s`, 'Gesamtzeit'];
                          return [value, name];
                        }}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            const roleName = payload[0].payload.roleName;
                            return `Rolle: ${ROLE_LABELS[roleName] || roleName}`;
                          }
                          return label;
                        }}
                      />
                      <Legend />
                      {Object.entries(ROLE_LABELS).map(([roleName, roleLabel]) => {
                        const roleData = statistics.scatterData.filter(d => d.roleName === roleName);
                        if (roleData.length === 0) return null;
                        
                        const isSelected = selectedRoles.includes(roleName);
                        
                        // Farben für verschiedene Rollen (deutlich unterschiedlich)
                        const colors = {
                          admin: '#2563eb',        // Kräftiges Blau
                          scorekeeper: '#059669',  // Kräftiges Grün
                          caller: '#dc2626',       // Kräftiges Rot
                          superadmin: '#7c3aed',   // Kräftiges Lila
                          user: '#f59e0b'          // Orange/Gold
                        };
                        
                        // Wenn nicht ausgewählt, grau verwenden
                        const fillColor = isSelected ? (colors[roleName] || '#64748b') : '#94a3b8';
                        
                        return (
                          <Scatter
                            key={roleName}
                            name={roleLabel}
                            data={roleData}
                            fill={fillColor}
                            opacity={isSelected ? 1 : 0.5}
                          />
                        );
                      })}
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {!statistics && !loading && !error && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-chart-line text-slate-400 dark:text-slate-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                  Keine Daten verfügbar
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Es sind noch keine abgeschlossenen Usability-Tests vorhanden.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default UsabilityStatisticsTailwind;
